import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSidebar } from '../contexts/SidebarContext';
import { useAuth } from '../contexts/AuthContext';
import mammoth from 'mammoth';
import { renderAsync } from 'docx-preview';
import { extractVariables, VariableInfo } from '../utils/docxParser';
import { apiService } from '../services/api';
import { API_BASE_URL } from '../config';

interface TemplateFormData {
  categoryId: number;
  agreementTypeId: number;
  title: string;
  description: string;
  templateType?: string;
  category?: any;
  agreementType?: any;
  mode?: 'create' | 'use';
  templateId?: number;
  filePath?: string;
  fileName?: string;
  variables?: any[];
}

const NewTemplate: React.FC = () => {
  const { isCollapsed } = useSidebar();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [formData, setFormData] = useState<TemplateFormData | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileArrayBuffer, setFileArrayBuffer] = useState<ArrayBuffer | null>(null);
  const [variableList, setVariableList] = useState<VariableInfo[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const previewContainerRef = useRef<HTMLDivElement>(null);

  // Load groups saat component mount
  useEffect(() => {
    const loadGroups = async () => {
      setLoadingGroups(true);
      try {
        const groupsData = await apiService.getGroups();
        setGroups(groupsData);
      } catch (err: any) {
        console.error('Error loading groups:', err);
        setError('Gagal memuat daftar groups');
      } finally {
        setLoadingGroups(false);
      }
    };
    loadGroups();
  }, []);

  // Check user scopes
  const hasTemplatesEditor = () => {
    return user?.scopes?.includes('templates.editor') || false;
  };

  // Ambil data dari location.state
  useEffect(() => {
    // Check permission first
    if (!hasTemplatesEditor()) {
      navigate('/templates', { replace: true });
      return;
    }

    const state = location.state as TemplateFormData | null;
    if (!state || !state.categoryId || !state.agreementTypeId || !state.title) {
      // Jika tidak ada data, redirect kembali ke templates
      navigate('/templates');
      return;
    }
    setFormData(state);
    
    // Jika mode 'use' dan ada filePath, load file dari backend
    if (state.mode === 'use' && state.filePath && state.variables) {
      loadTemplateFile(state.filePath, state.variables);
    }
  }, [location, navigate, user]);

  const loadTemplateFile = async (filePath: string, variables: any[]) => {
    setIsLoading(true);
    setError('');
    
    try {
      // Fetch file dari backend - filePath sudah relatif dari backend
      const response = await fetch(`${API_BASE_URL}${filePath}`);
      if (!response.ok) {
        throw new Error('Gagal memuat file template');
      }
      
      const arrayBuffer = await response.arrayBuffer();
      setFileArrayBuffer(arrayBuffer);
      
      // Set variables dari template
      const variableList: VariableInfo[] = variables.map((v: any) => ({
        name: v.name,
        position: v.position || 0,
        group_id: v.group_id || null
      }));
      setVariableList(variableList);
      
      // Create a dummy File object untuk state
      const dummyFile = new File([arrayBuffer], formData?.fileName || 'template.docx', {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      setFile(dummyFile);
    } catch (err: any) {
      console.error('Error loading template file:', err);
      setError('Gagal memuat file template');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = useCallback(async (selectedFile: File) => {
    // Validasi file type - hanya .docx yang didukung dengan baik oleh docx-preview
    const isDocx = selectedFile.name.toLowerCase().endsWith('.docx') || 
                   selectedFile.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    
    if (!isDocx) {
      setError('File harus berformat .docx (format .doc tidak didukung)');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Baca file sebagai array buffer
      const arrayBuffer = await selectedFile.arrayBuffer();
      
      setFile(selectedFile);
      setFileArrayBuffer(arrayBuffer);

      // Extract variables dari dokumen (konversi ke text dulu)
      const result = await mammoth.extractRawText({ arrayBuffer });
      const text = result.value;
      
      // Extract variables dari text
      const extractedVars = extractVariables(text);
      setVariableList(extractedVars);
    } catch (err) {
      console.error('Error reading file:', err);
      setError('Gagal membaca file. Pastikan file adalah dokumen Word yang valid.');
      setFile(null);
      setFileArrayBuffer(null);
      setVariableList([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Render dokumen saat file dan container sudah ready
  useEffect(() => {
    if (!fileArrayBuffer || !file || isLoading) {
      return;
    }

    const renderDocument = async () => {
      // Tunggu sampai container benar-benar ada di DOM dengan retry mechanism
      let retryCount = 0;
      const maxRetries = 20; // Max 2 detik (20 * 100ms)

      const checkAndRender = async () => {
        if (!previewContainerRef.current) {
          retryCount++;
          if (retryCount < maxRetries) {
            console.log(`Container belum tersedia, retry ${retryCount}/${maxRetries}...`);
            setTimeout(checkAndRender, 100);
            return;
          } else {
            console.error('Container tidak ditemukan setelah beberapa retry');
            return;
          }
        }

        console.log('Container found, starting render...');
        console.log('File:', file.name);
        console.log('ArrayBuffer size:', fileArrayBuffer.byteLength);
        console.log('Container element:', previewContainerRef.current);

        try {
          // Clear previous content
          previewContainerRef.current.innerHTML = '';
          
          console.log('Calling renderAsync...');
          
          // Render dokumen Word menggunakan docx-preview
          // Parameter: document, bodyContainer, styleContainer, options
          const result = await renderAsync(
            fileArrayBuffer, 
            previewContainerRef.current, 
            undefined, // styleContainer - undefined berarti menggunakan bodyContainer
            {
              className: 'docx-wrapper',
              inWrapper: true,
              ignoreWidth: false,
              ignoreHeight: false,
              breakPages: true,
              ignoreLastRenderedPageBreak: true,
              experimental: false,
              trimXmlDeclaration: true,
              debug: true, // Enable debug untuk melihat proses
            }
          );
          
          console.log('Document rendered successfully', result);
          console.log('Container innerHTML length:', previewContainerRef.current.innerHTML.length);
        } catch (err) {
          console.error('Error rendering document:', err);
          console.error('Error details:', {
            message: err instanceof Error ? err.message : String(err),
            stack: err instanceof Error ? err.stack : undefined
          });
          
          if (previewContainerRef.current) {
            const errorMessage = err instanceof Error ? err.message : String(err);
            previewContainerRef.current.innerHTML = `
              <div style="display: flex; align-items: center; justify-content: center; height: 100%; padding: 2rem; text-align: center; color: #4b5563;">
                <div>
                  <i class="fa-solid fa-exclamation-triangle" style="font-size: 3rem; color: #eab308; margin-bottom: 1rem;"></i>
                  <p style="margin-bottom: 0.5rem; font-weight: 500;">Gagal menampilkan preview dokumen</p>
                  <p style="font-size: 0.875rem; color: #6b7280;">${errorMessage}</p>
                  <p style="font-size: 0.75rem; color: #9ca3af; margin-top: 0.5rem;">Pastikan file adalah dokumen Word (.docx) yang valid</p>
                </div>
              </div>
            `;
          }
        }
      };

      // Mulai check setelah delay kecil untuk memastikan DOM sudah ter-render
      setTimeout(checkAndRender, 200);
    };

    renderDocument();
  }, [fileArrayBuffer, isLoading, file]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleReset = () => {
    setFile(null);
    setFileArrayBuffer(null);
    if (previewContainerRef.current) {
      previewContainerRef.current.innerHTML = '';
    }
    setVariableList([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    // Jika mode 'use', redirect ke halaman create agreement dengan template data
    if (formData?.mode === 'use') {
      // TODO: Redirect ke halaman create agreement dengan template data
      navigate('/new-request', {
        state: {
          templateId: formData.templateId,
          templateData: {
            title: formData.title,
            description: formData.description,
            categoryId: formData.categoryId,
            agreementTypeId: formData.agreementTypeId,
            variables: variableList,
            filePath: formData.filePath
          }
        }
      });
      return;
    }

    // Mode 'create' - save template baru
    if (!file || !formData) {
      setError('File dan form data harus diisi');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Buat FormData untuk multipart/form-data
      const formDataToSend = new FormData();
      
      // Pastikan file ada
      if (!file) {
        throw new Error('File tidak ditemukan');
      }
      
      formDataToSend.append('file', file);
      formDataToSend.append('title', formData.title);
      
      if (formData.description) {
        formDataToSend.append('description', formData.description);
      }
      
      formDataToSend.append('category_id', formData.categoryId.toString());
      formDataToSend.append('agreement_type_id', formData.agreementTypeId.toString());
      formDataToSend.append('template_type', formData.templateType || 'Internal');
      
      // Convert variables to JSON string
      if (variableList.length > 0) {
        const variablesJson = JSON.stringify(variableList);
        formDataToSend.append('variables', variablesJson);
      }

      // Debug: Log FormData contents
      console.log('FormData contents:');
      console.log('File:', file.name, file.size, file.type);
      console.log('Title:', formData.title);
      console.log('Description:', formData.description);
      console.log('Category ID:', formData.categoryId);
      console.log('Agreement Type ID:', formData.agreementTypeId);
      console.log('Variables:', variableList.length);
      
      // Verify FormData entries
      console.log('FormData entries:');
      Array.from(formDataToSend.entries()).forEach(([key, value]) => {
        if (value instanceof File) {
          console.log(`${key}:`, value.name, value.size, value.type);
        } else {
          console.log(`${key}:`, value);
        }
      });

      // Call API
      const result = await apiService.createTemplate(formDataToSend);
      
      console.log('Template saved successfully:', result);
      
      // Redirect ke halaman templates dengan success message
      navigate('/templates', { 
        state: { 
          message: 'Template berhasil disimpan!',
          success: true 
        } 
      });
    } catch (err: any) {
      console.error('Error saving template:', err);
      setError(err.message || 'Gagal menyimpan template. Silakan coba lagi.');
    } finally {
      setIsLoading(false);
    }
  };

  // Cleanup saat component unmount
  useEffect(() => {
    return () => {
      if (previewContainerRef.current) {
        previewContainerRef.current.innerHTML = '';
      }
    };
  }, []);

  // Jika formData belum dimuat, tampilkan loading
  if (!formData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <section 
        className="bg-white border-b border-gray-200 px-6 py-4 fixed z-30 transition-all duration-300"
        style={{ 
          left: isCollapsed ? '64px' : '256px',
          right: 0,
          top: '56px'
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <button
              onClick={() => navigate('/templates')}
              className="text-gray-600 hover:text-gray-900"
            >
              <i className="fa-solid fa-arrow-left text-lg"></i>
            </button>
            <div className="flex-1">
              <h1 className="text-2xl font-semibold text-gray-900">{formData.title}</h1>
              <div className="flex items-center space-x-4 mt-1 text-sm text-gray-600">
                {formData.description && (
                  <span className="flex items-center">
                    <i className="fa-solid fa-file-lines mr-1"></i>
                    {formData.description}
                  </span>
                )}
                {formData.category && (
                  <span className="flex items-center">
                    <i className="fa-solid fa-folder mr-1"></i>
                    {formData.category.name}
                  </span>
                )}
                {formData.agreementType && (
                  <span className="flex items-center">
                    <i className="fa-solid fa-file-signature mr-1"></i>
                    {formData.agreementType.name}
                  </span>
                )}
              </div>
            </div>
          </div>
          {file && (
            <div className="flex items-center space-x-3">
              <button
                onClick={handleReset}
                className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50"
              >
                <i className="fa-solid fa-rotate-left mr-2"></i>
                Reset
              </button>
              <button
                onClick={handleSave}
                disabled={isLoading}
                className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className={`fa-solid ${formData?.mode === 'use' ? 'fa-check' : 'fa-save'} mr-2`}></i>
                {formData?.mode === 'use' ? 'Use this template' : 'Save Template'}
              </button>
            </div>
          )}
        </div>
      </section>

      {error && (
        <div className="px-6 py-3 bg-red-50 border-l-4 border-red-500 text-red-700" style={{ marginTop: '100px' }}>
          <div className="flex items-center">
            <i className="fa-solid fa-exclamation-circle mr-2"></i>
            <span>{error}</span>
          </div>
        </div>
      )}

      <div style={{ 
        marginTop: error ? '120px' : '100px', 
        height: error ? 'calc(100svh - 56px - 64px - 120px)' : 'calc(100svh - 56px - 64px - 100px)', 
        overflow: 'hidden',
        maxHeight: error ? 'calc(100svh - 56px - 64px - 120px)' : 'calc(100svh - 56px - 64px - 100px)'
      }}>
        {!file && formData?.mode !== 'use' ? (
          // Upload Section
          <div className="flex items-center justify-center h-full px-6">
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className="w-full max-w-2xl border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-violet-500 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx,.doc"
                onChange={handleFileChange}
                className="hidden"
              />
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mb-4">
                  <i className="fa-solid fa-file-word text-violet-600 text-3xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Upload Dokumen Word
                </h3>
                <p className="text-gray-600 mb-4">
                  Drag & drop file atau klik untuk memilih file
                </p>
                <p className="text-sm text-gray-500">
                  Format yang didukung: .docx
                </p>
                {error && (
                  <div className="mt-4 px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {error}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          // Preview Section dengan dua kolom
          <div className="flex h-full">
            {/* Preview Section */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-sm font-semibold text-gray-700">Preview Dokumen</h2>
                <p className="text-xs text-gray-500 mt-1">File: {file?.name}</p>
              </div>
              <div 
                ref={previewContainerRef}
                className="docx-preview-container flex-1 overflow-y-auto overflow-x-hidden p-8 bg-gray-100"
                style={{
                  minHeight: 0
                }}
              >
                {isLoading && (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Memuat preview dokumen...</p>
                    </div>
                  </div>
                )}
                {!isLoading && !fileArrayBuffer && (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <i className="fa-solid fa-file-word text-gray-400 text-4xl mb-4"></i>
                      <p className="text-gray-600">Browser tidak mendukung preview dokumen Word</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Variables Section */}
            <div className="w-80 border-l border-gray-200 bg-white overflow-y-auto flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
                <h2 className="text-sm font-semibold text-gray-700">Variables</h2>
                <p className="text-xs text-gray-500 mt-1">{variableList.length} variables ditemukan</p>
              </div>
              <div className="p-6 flex-1 overflow-y-auto">
                {variableList.length === 0 ? (
                  <div className="text-center py-8">
                    <i className="fa-solid fa-info-circle text-gray-400 text-2xl mb-2"></i>
                    <p className="text-sm text-gray-500">Tidak ada variables ditemukan</p>
                    <p className="text-xs text-gray-400 mt-1">Gunakan format {'{variable_name}'} dalam dokumen</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {variableList
                      .sort((a, b) => (a.position || 0) - (b.position || 0))
                      .map((variable, index) => {
                        const sortedIndex = variableList
                          .sort((a, b) => (a.position || 0) - (b.position || 0))
                          .findIndex((v) => v.name === variable.name);
                        return (
                          <div
                            key={`${variable.name}-${variable.position}`}
                            className="p-3 bg-violet-50 border border-violet-200 rounded-lg"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-violet-900 break-all">
                                  {variable.name}
                                </div>
                              </div>
                              <span className="text-xs text-violet-600 ml-2 flex-shrink-0">
                                #{sortedIndex + 1}
                              </span>
                            </div>
                            <div className="mt-2">
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                PIC Pengisi:
                              </label>
                              <select
                                value={variable.group_id || ''}
                                onChange={(e) => {
                                  const newVariableList = [...variableList];
                                  const variableIndex = variableList.findIndex((v) => v.name === variable.name);
                                  newVariableList[variableIndex] = {
                                    ...variable,
                                    group_id: e.target.value ? Number(e.target.value) : null
                                  };
                                  setVariableList(newVariableList);
                                }}
                                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white"
                                disabled={loadingGroups}
                              >
                                <option value="">-- Pilih Group --</option>
                                {groups
                                  .filter((g: any) => g.is_active !== false)
                                  .map((group: any) => (
                                    <option key={group.id} value={group.id}>
                                      {group.name}
                                    </option>
                                  ))}
                              </select>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default NewTemplate;
